import { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";

const OWNERSHIP_OPTIONS = ["Company", "Owner Operator's", "Contractor Owned"];
const PURCHASE_OPTIONS = [
  "N/A",
  "Company Owned / Paid Off",
  "Rental",
  "Leased",
  "Lease to Own",
  "Financed",
];
const STATUS_OPTIONS = ["active", "inactive", "sold"];
const US_STATES = [
  { abbr: "AL", name: "Alabama" },
  { abbr: "AK", name: "Alaska" },
  { abbr: "AZ", name: "Arizona" },
  { abbr: "AR", name: "Arkansas" },
  { abbr: "CA", name: "California" },
  { abbr: "CO", name: "Colorado" },
  { abbr: "CT", name: "Connecticut" },
  { abbr: "DE", name: "Delaware" },
  { abbr: "FL", name: "Florida" },
  { abbr: "GA", name: "Georgia" },
  { abbr: "HI", name: "Hawaii" },
  { abbr: "ID", name: "Idaho" },
  { abbr: "IL", name: "Illinois" },
  { abbr: "IN", name: "Indiana" },
  { abbr: "IA", name: "Iowa" },
  { abbr: "KS", name: "Kansas" },
  { abbr: "KY", name: "Kentucky" },
  { abbr: "LA", name: "Louisiana" },
  { abbr: "ME", name: "Maine" },
  { abbr: "MD", name: "Maryland" },
  { abbr: "MA", name: "Massachusetts" },
  { abbr: "MI", name: "Michigan" },
  { abbr: "MN", name: "Minnesota" },
  { abbr: "MS", name: "Mississippi" },
  { abbr: "MO", name: "Missouri" },
  { abbr: "MT", name: "Montana" },
  { abbr: "NE", name: "Nebraska" },
  { abbr: "NV", name: "Nevada" },
  { abbr: "NH", name: "New Hampshire" },
  { abbr: "NJ", name: "New Jersey" },
  { abbr: "NM", name: "New Mexico" },
  { abbr: "NY", name: "New York" },
  { abbr: "NC", name: "North Carolina" },
  { abbr: "ND", name: "North Dakota" },
  { abbr: "OH", name: "Ohio" },
  { abbr: "OK", name: "Oklahoma" },
  { abbr: "OR", name: "Oregon" },
  { abbr: "PA", name: "Pennsylvania" },
  { abbr: "RI", name: "Rhode Island" },
  { abbr: "SC", name: "South Carolina" },
  { abbr: "SD", name: "South Dakota" },
  { abbr: "TN", name: "Tennessee" },
  { abbr: "TX", name: "Texas" },
  { abbr: "UT", name: "Utah" },
  { abbr: "VT", name: "Vermont" },
  { abbr: "VA", name: "Virginia" },
  { abbr: "WA", name: "Washington" },
  { abbr: "WV", name: "West Virginia" },
  { abbr: "WI", name: "Wisconsin" },
  { abbr: "WY", name: "Wyoming" },
];

const inputStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
};
const selectStyle = { ...inputStyle, background: "#fff", cursor: "pointer" };
const labelStyle = {
  fontSize: "11px",
  fontWeight: "500",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
};
const sectionHeaderStyle = {
  fontSize: "13px",
  fontWeight: "600",
  color: "#111",
  marginBottom: "14px",
  paddingBottom: "8px",
  borderBottom: "0.5px solid #e8e8e8",
};

const DEFAULT_FORM = {
  unit_number: "",
  in_service_date: "",
  vin: "",
  license_plate: "",
  state: "",
  make: "",
  year: "",
  gross_weight: "",
  num_axles: "",
  unladen_weight: "",
  color: "",
  ownership: "",
  purchase_type: "N/A",
  assigned_drivers: [],
  fuel_card: "",
  tolls_prepass: "",
  eld_device: "",
  camera_device: "",
  cab_other: "",
  ifta_sticker: "",
  hut_sticker: "",
  notes: "",
  status: "active",
};

function getStateName(abbr) {
  if (!abbr) return "—";
  const s = US_STATES.find((s) => s.abbr === abbr);
  return s ? `${s.abbr} — ${s.name}` : abbr;
}

// ── Rich text editor ──
function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);
  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || "");
  };
  const btnS = () => ({
    background: "#f5f5f5",
    border: "0.5px solid #e0e0e0",
    borderRadius: "5px",
    padding: "4px 8px",
    fontSize: "12px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: "500",
    color: "#333",
  });
  return (
    <div
      style={{
        border: "0.5px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "4px",
          padding: "6px 8px",
          background: "#fafafa",
          borderBottom: "0.5px solid #e0e0e0",
          flexWrap: "wrap",
        }}
      >
        <button type="button" onClick={() => exec("bold")} style={btnS()}>
          <b>B</b>
        </button>
        <button type="button" onClick={() => exec("italic")} style={btnS()}>
          <i>I</i>
        </button>
        <button type="button" onClick={() => exec("underline")} style={btnS()}>
          <u>U</u>
        </button>
        <div
          style={{ width: "0.5px", background: "#e0e0e0", margin: "0 4px" }}
        />
        <button
          type="button"
          onClick={() => exec("justifyLeft")}
          style={btnS()}
        >
          ≡L
        </button>
        <button
          type="button"
          onClick={() => exec("justifyCenter")}
          style={btnS()}
        >
          ≡C
        </button>
        <button
          type="button"
          onClick={() => exec("justifyRight")}
          style={btnS()}
        >
          ≡R
        </button>
        <div
          style={{ width: "0.5px", background: "#e0e0e0", margin: "0 4px" }}
        />
        <button
          type="button"
          onClick={() => exec("insertUnorderedList")}
          style={btnS()}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => exec("insertOrderedList")}
          style={btnS()}
        >
          1. List
        </button>
        <div
          style={{ width: "0.5px", background: "#e0e0e0", margin: "0 4px" }}
        />
        <select
          onChange={(e) => exec("formatBlock", e.target.value)}
          style={{
            ...selectStyle,
            width: "auto",
            padding: "3px 6px",
            fontSize: "11px",
          }}
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || "")}
        style={{
          padding: "10px 12px",
          minHeight: "100px",
          fontSize: "13px",
          color: "#111",
          outline: "none",
          lineHeight: "1.6",
        }}
      />
    </div>
  );
}

// ── State lookup ──
function StateLookup({ value, onSelect }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    setQuery(value || "");
  }, [value]);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = US_STATES.filter(
    (s) =>
      s.abbr.toLowerCase().includes(query.toLowerCase()) ||
      s.name.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 10);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        placeholder="Search state..."
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value) onSelect("");
        }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />
      {open && query.length > 0 && filtered.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "0.5px solid #e0e0e0",
            borderRadius: "8px",
            zIndex: 200,
            marginTop: "4px",
            overflow: "hidden",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          {filtered.map((s) => (
            <div
              key={s.abbr}
              onMouseDown={() => {
                onSelect(s.abbr);
                setQuery(s.abbr);
                setOpen(false);
              }}
              style={{
                padding: "9px 14px",
                cursor: "pointer",
                fontSize: "13px",
                color: "#111",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: value === s.abbr ? "#f0faf4" : "#fff",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  value === s.abbr ? "#f0faf4" : "#fff")
              }
            >
              <div
                style={{
                  width: "32px",
                  height: "24px",
                  borderRadius: "4px",
                  background: "#eaf3de",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  color: "#3a9e5f",
                  flexShrink: 0,
                }}
              >
                {s.abbr}
              </div>
              <div style={{ fontWeight: value === s.abbr ? "500" : "400" }}>
                {s.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Driver multi-select ──
function DriverMultiSelect({ drivers, selected, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const selectedDrivers = drivers.filter((d) => selected.includes(d.id));
  const filtered = drivers
    .filter(
      (d) =>
        !selected.includes(d.id) &&
        `${d.first_name} ${d.last_name}`
          .toLowerCase()
          .includes(query.toLowerCase())
    )
    .slice(0, 8);
  return (
    <div ref={ref}>
      {selectedDrivers.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "8px",
          }}
        >
          {selectedDrivers.map((d) => (
            <div
              key={d.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "#eaf3de",
                color: "#3b6d11",
                borderRadius: "20px",
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              {d.first_name} {d.last_name}
              <button
                onMouseDown={() =>
                  onChange(selected.filter((id) => id !== d.id))
                }
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#3b6d11",
                  fontSize: "14px",
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <input
          style={inputStyle}
          placeholder="Search and add drivers..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && query.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "0.5px solid #e0e0e0",
              borderRadius: "8px",
              zIndex: 200,
              maxHeight: "180px",
              overflowY: "auto",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              marginTop: "4px",
              overflow: "hidden",
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: "12px",
                  color: "#9ca3af",
                }}
              >
                No drivers found
              </div>
            ) : (
              filtered.map((d) => (
                <div
                  key={d.id}
                  onMouseDown={() => {
                    onChange([...selected, d.id]);
                    setQuery("");
                    setOpen(false);
                  }}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#111",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#fff")
                  }
                >
                  <div
                    style={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      background: "#eaf3de",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      fontWeight: "500",
                      color: "#3a9e5f",
                      flexShrink: 0,
                    }}
                  >
                    {d.first_name?.[0]}
                    {d.last_name?.[0]}
                  </div>
                  {d.first_name} {d.last_name}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Field wrapper ──
function Field({ label, children, required }) {
  return (
    <div>
      <div style={labelStyle}>
        {label}
        {required && <span style={{ color: "#a32d2d" }}> *</span>}
      </div>
      {children}
    </div>
  );
}

// ── Form content — outside Vehicles to prevent remount on every keystroke ──
function FormContent({ form, setF, drivers, photos, setPhotos }) {
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - photos.length;
    const toAdd = files.slice(0, remaining);
    const previews = toAdd.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));
    setPhotos((p) => [...p, ...previews]);
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <div style={sectionHeaderStyle}>Unit Details</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <Field label="Unit #" required>
            <input
              style={inputStyle}
              value={form.unit_number}
              onChange={(e) => setF("unit_number", e.target.value)}
              placeholder="e.g. 101"
            />
          </Field>
          <Field label="In Service Date" required>
            <input
              type="date"
              style={inputStyle}
              value={form.in_service_date}
              onChange={(e) => setF("in_service_date", e.target.value)}
            />
          </Field>
          <Field label="VIN #">
            <input
              style={inputStyle}
              value={form.vin}
              onChange={(e) => setF("vin", e.target.value)}
              placeholder="17-character VIN"
            />
          </Field>
          <Field label="License Plate #" required>
            <input
              style={inputStyle}
              value={form.license_plate}
              onChange={(e) => setF("license_plate", e.target.value)}
              placeholder="e.g. ABC1234"
            />
          </Field>
          <Field label="State" required>
            <StateLookup
              value={form.state}
              onSelect={(v) => setF("state", v)}
            />
            {form.state && (
              <div
                style={{ marginTop: "4px", fontSize: "11px", color: "#3a9e5f" }}
              >
                ✓ {getStateName(form.state)}
              </div>
            )}
          </Field>
          <Field label="Make" required>
            <input
              style={inputStyle}
              value={form.make}
              onChange={(e) => setF("make", e.target.value)}
              placeholder="e.g. Freightliner"
            />
          </Field>
          <Field label="Year" required>
            <input
              type="number"
              style={inputStyle}
              value={form.year}
              onChange={(e) => setF("year", e.target.value)}
              placeholder="e.g. 2022"
              min="1990"
              max="2030"
            />
          </Field>
          <Field label="Color">
            <input
              style={inputStyle}
              value={form.color}
              onChange={(e) => setF("color", e.target.value)}
              placeholder="e.g. White"
            />
          </Field>
          <Field label="Gross Weight">
            <input
              style={inputStyle}
              value={form.gross_weight}
              onChange={(e) => setF("gross_weight", e.target.value)}
              placeholder="e.g. 80,000 lbs"
            />
          </Field>
          <Field label="Number of Axles">
            <input
              style={inputStyle}
              value={form.num_axles}
              onChange={(e) => setF("num_axles", e.target.value)}
              placeholder="e.g. 5"
            />
          </Field>
          <Field label="Unladen Weight">
            <input
              style={inputStyle}
              value={form.unladen_weight}
              onChange={(e) => setF("unladen_weight", e.target.value)}
              placeholder="e.g. 35,000 lbs"
            />
          </Field>
          <Field label="Ownership" required>
            <select
              style={selectStyle}
              value={form.ownership}
              onChange={(e) => setF("ownership", e.target.value)}
            >
              <option value="">Select ownership</option>
              {OWNERSHIP_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div style={{ marginTop: "12px" }}>
          <Field label="Assigned Drivers">
            <DriverMultiSelect
              drivers={drivers}
              selected={form.assigned_drivers}
              onChange={(v) => setF("assigned_drivers", v)}
            />
          </Field>
        </div>
      </div>

      <div>
        <div style={sectionHeaderStyle}>Other Details</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <Field label="Purchase Type">
            <select
              style={selectStyle}
              value={form.purchase_type}
              onChange={(e) => setF("purchase_type", e.target.value)}
            >
              {PURCHASE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select
              style={selectStyle}
              value={form.status}
              onChange={(e) => setF("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div>
        <div style={sectionHeaderStyle}>In-Cab Inventory</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <Field label="Fuel Card">
            <input
              style={inputStyle}
              value={form.fuel_card}
              onChange={(e) => setF("fuel_card", e.target.value)}
              placeholder="Card # or provider"
            />
          </Field>
          <Field label="Tolls & PrePass Device">
            <input
              style={inputStyle}
              value={form.tolls_prepass}
              onChange={(e) => setF("tolls_prepass", e.target.value)}
              placeholder="Device # or transponder #"
            />
          </Field>
          <Field label="ELD Device(s)">
            <input
              style={inputStyle}
              value={form.eld_device}
              onChange={(e) => setF("eld_device", e.target.value)}
              placeholder="Device ID or serial #"
            />
          </Field>
          <Field label="Camera Device(s)">
            <input
              style={inputStyle}
              value={form.camera_device}
              onChange={(e) => setF("camera_device", e.target.value)}
              placeholder="Device ID or serial #"
            />
          </Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Other">
              <input
                style={inputStyle}
                value={form.cab_other}
                onChange={(e) => setF("cab_other", e.target.value)}
                placeholder="Any other in-cab equipment"
              />
            </Field>
          </div>
        </div>
      </div>

      <div>
        <div style={sectionHeaderStyle}>Stickers</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <Field label="IFTA Sticker #">
            <input
              style={inputStyle}
              value={form.ifta_sticker}
              onChange={(e) => setF("ifta_sticker", e.target.value)}
              placeholder="IFTA sticker number"
            />
          </Field>
          <Field label="HUT Sticker #">
            <input
              style={inputStyle}
              value={form.hut_sticker}
              onChange={(e) => setF("hut_sticker", e.target.value)}
              placeholder="HUT sticker number"
            />
          </Field>
        </div>
      </div>

      <div>
        <div style={sectionHeaderStyle}>Truck Photos</div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "10px",
          }}
        >
          {photos.map((p, i) => (
            <div
              key={i}
              style={{ position: "relative", width: "100px", height: "75px" }}
            >
              <img
                src={p.preview}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "6px",
                  border: "0.5px solid #e8e8e8",
                }}
              />
              <button
                onClick={() => removePhoto(i)}
                style={{
                  position: "absolute",
                  top: "3px",
                  right: "3px",
                  background: "rgba(0,0,0,0.6)",
                  border: "none",
                  borderRadius: "50%",
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: "11px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
          ))}
          {photos.length < 5 && (
            <label
              style={{
                width: "100px",
                height: "75px",
                border: "1.5px dashed #e0e0e0",
                borderRadius: "6px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#9ca3af",
                fontSize: "11px",
                gap: "4px",
              }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handlePhotoSelect}
              />
              <span style={{ fontSize: "20px" }}>+</span>
              <span>Add photo</span>
            </label>
          )}
        </div>
        <div style={{ fontSize: "11px", color: "#9ca3af" }}>
          {photos.length}/5 photos
        </div>
      </div>

      <div>
        <div style={sectionHeaderStyle}>Additional Notes</div>
        <RichTextEditor value={form.notes} onChange={(v) => setF("notes", v)} />
      </div>
    </div>
  );
}

export default function Vehicles() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchTrucks();
    fetchDrivers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchTrucks = async () => {
    const { data } = await supabase
      .from("trucks")
      .select("*")
      .order("created_at", { ascending: false });
    setTrucks(data || []);
    setLoading(false);
  };

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("id, first_name, last_name")
      .order("first_name");
    setDrivers(data || []);
  };

  const fetchTruckPhotos = async (truckId) => {
    const { data } = await supabase
      .from("truck_photos")
      .select("*")
      .eq("truck_id", truckId)
      .order("created_at");
    setDetailPhotos(data || []);
  };

  const setF = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const resetForm = () => {
    setForm({ ...DEFAULT_FORM });
    setPhotos([]);
  };

  const uploadPhoto = async (file, truckId) => {
    const ext = file.name.split(".").pop();
    const fileName = `trucks/${truckId}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from("driver-docs")
      .upload(fileName, file, { upsert: true });
    if (!error) {
      const { data: urlData } = supabase.storage
        .from("driver-docs")
        .getPublicUrl(fileName);
      await supabase
        .from("truck_photos")
        .insert({ truck_id: truckId, url: urlData.publicUrl });
      return urlData.publicUrl;
    }
    return null;
  };

  const handleSave = async () => {
    if (
      !form.unit_number ||
      !form.in_service_date ||
      !form.license_plate ||
      !form.state ||
      !form.make ||
      !form.year ||
      !form.ownership
    ) {
      alert("Please fill in all mandatory fields");
      return;
    }
    setSaving(true);
    const payload = {
      unit_number: form.unit_number,
      in_service_date: form.in_service_date,
      vin: form.vin || null,
      license_plate: form.license_plate,
      state: form.state,
      make: form.make,
      year: parseInt(form.year),
      gross_weight: form.gross_weight || null,
      num_axles: form.num_axles || null,
      unladen_weight: form.unladen_weight || null,
      color: form.color || null,
      ownership: form.ownership,
      purchase_type: form.purchase_type || "N/A",
      assigned_drivers: form.assigned_drivers || [],
      fuel_card: form.fuel_card || null,
      tolls_prepass: form.tolls_prepass || null,
      eld_device: form.eld_device || null,
      camera_device: form.camera_device || null,
      cab_other: form.cab_other || null,
      ifta_sticker: form.ifta_sticker || null,
      hut_sticker: form.hut_sticker || null,
      notes: form.notes || null,
      status: form.status || "active",
    };
    if (editMode && selectedTruck) {
      await supabase.from("trucks").update(payload).eq("id", selectedTruck.id);
      if (photos.length > 0) {
        setUploadingPhoto(true);
        for (const p of photos) {
          if (p.file) await uploadPhoto(p.file, selectedTruck.id);
        }
        setUploadingPhoto(false);
        fetchTruckPhotos(selectedTruck.id);
      }
      setSelectedTruck({ ...selectedTruck, ...payload });
      setEditMode(false);
    } else {
      const { data, error } = await supabase
        .from("trucks")
        .insert(payload)
        .select()
        .single();
      if (!error && data && photos.length > 0) {
        setUploadingPhoto(true);
        for (const p of photos) {
          if (p.file) await uploadPhoto(p.file, data.id);
        }
        setUploadingPhoto(false);
      }
      setShowModal(false);
      resetForm();
    }
    fetchTrucks();
    setSaving(false);
  };

  const openDetail = async (truck) => {
    setSelectedTruck(truck);
    setForm({ ...DEFAULT_FORM, ...truck });
    setShowDetail(true);
    setEditMode(false);
    fetchTruckPhotos(truck.id);
  };

  const deletePhoto = async (photoId) => {
    await supabase.from("truck_photos").delete().eq("id", photoId);
    setDetailPhotos((p) => p.filter((ph) => ph.id !== photoId));
  };

  const getDriverNames = (driverIds) => {
    if (!driverIds || driverIds.length === 0) return "—";
    return driverIds
      .map((id) => {
        const d = drivers.find((dr) => dr.id === id);
        return d ? `${d.first_name} ${d.last_name}` : "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const getStatusStyle = (status) => {
    const map = {
      active: { background: "#eaf3de", color: "#3b6d11" },
      inactive: { background: "#faeeda", color: "#854f0b" },
      sold: { background: "#f0f0f0", color: "#6b7280" },
    };
    return map[status] || map.active;
  };

  const filteredTrucks = trucks.filter((t) => {
    const matchStatus = filterStatus === "all" || t.status === filterStatus;
    const matchSearch =
      !search ||
      t.unit_number?.toLowerCase().includes(search.toLowerCase()) ||
      t.make?.toLowerCase().includes(search.toLowerCase()) ||
      t.license_plate?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile && <MobileHeader title="Vehicles" />}

        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="t-page-title">Fleet Overview</div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              placeholder="Search unit, make, plate..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                fontSize: "13px",
                border: "0.5px solid #e0e0e0",
                borderRadius: "8px",
                padding: "7px 12px",
                width: "220px",
                outline: "none",
                color: "#111",
              }}
            />
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              style={{
                background: "#1a1e1c",
                color: "#fff",
                fontSize: "13px",
                fontWeight: "500",
                padding: "7px 14px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              + Add vehicle
            </button>
          </div>
        </div>

        <div style={{ padding: isMobile ? "12px" : "20px 24px" }}>
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            {["all", "active", "inactive", "sold"].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                style={{
                  padding: "5px 14px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                  border: "0.5px solid",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  background: filterStatus === s ? "#3a9e5f" : "#fff",
                  color: filterStatus === s ? "#fff" : "#666",
                  borderColor: filterStatus === s ? "#3a9e5f" : "#e0e0e0",
                }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
                <span
                  style={{ marginLeft: "6px", fontSize: "11px", opacity: 0.75 }}
                >
                  {s === "all"
                    ? trucks.length
                    : trucks.filter((t) => t.status === s).length}
                </span>
              </button>
            ))}
          </div>

          <div
            style={{
              background: "#fff",
              border: "0.5px solid #e8e8e8",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {!isMobile && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 1.5fr 1fr 0.5fr",
                  padding: "10px 16px",
                  background: "#fafafa",
                  borderBottom: "0.5px solid #e8e8e8",
                }}
              >
                {[
                  "Make",
                  "Year",
                  "License Plate",
                  "State",
                  "Assigned To",
                  "Status",
                  "",
                ].map((h) => (
                  <div key={h} className="t-table-header">
                    {h}
                  </div>
                ))}
              </div>
            )}
            {loading ? (
              <div
                style={{ padding: "40px", textAlign: "center" }}
                className="t-body"
              >
                Loading...
              </div>
            ) : filteredTrucks.length === 0 ? (
              <div
                style={{ padding: "40px", textAlign: "center" }}
                className="t-body"
              >
                No vehicles yet — click Add vehicle to get started
              </div>
            ) : (
              filteredTrucks.map((truck) => (
                <div
                  key={truck.id}
                  onClick={() => openDetail(truck)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "1fr 1fr 1fr 1fr 1.5fr 1fr 0.5fr",
                    padding: "12px 16px",
                    borderBottom: "0.5px solid #f0f0f0",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#fafafa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#fff")
                  }
                >
                  <div>
                    <div className="t-name">{truck.make}</div>
                    <div className="t-label" style={{ color: "#9ca3af" }}>
                      Unit #{truck.unit_number}
                    </div>
                  </div>
                  <div className="t-data">{truck.year}</div>
                  <div className="t-data">{truck.license_plate}</div>
                  <div className="t-data">{truck.state}</div>
                  <div className="t-label" style={{ color: "#6b7280" }}>
                    {getDriverNames(truck.assigned_drivers)}
                  </div>
                  <div>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        padding: "2px 8px",
                        borderRadius: "20px",
                        ...getStatusStyle(truck.status),
                      }}
                    >
                      {truck.status}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#3a9e5f",
                      fontWeight: "500",
                    }}
                  >
                    View →
                  </div>
                </div>
              ))
            )}
          </div>
          <div
            className="t-label"
            style={{ marginTop: "10px", color: "#9ca3af" }}
          >
            {filteredTrucks.length} vehicle
            {filteredTrucks.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
              resetForm();
            }
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "680px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "0.5px solid #e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <div>
                <div className="t-name" style={{ fontSize: "15px" }}>
                  Add vehicle
                </div>
                <div
                  className="t-label"
                  style={{ color: "#9ca3af", marginTop: "2px" }}
                >
                  Fill in the vehicle details below
                </div>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "#f0f0f0",
                  border: "none",
                  borderRadius: "8px",
                  width: "30px",
                  height: "30px",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <FormContent
                form={form}
                setF={setF}
                drivers={drivers}
                photos={photos}
                setPhotos={setPhotos}
              />
            </div>
            <div
              style={{
                padding: "16px 24px",
                borderTop: "0.5px solid #e8e8e8",
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                position: "sticky",
                bottom: 0,
                background: "#fff",
              }}
            >
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "#f0f0f0",
                  color: "#555",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploadingPhoto}
                style={{
                  background: saving ? "#9ca3af" : "#3a9e5f",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {saving
                  ? "Saving..."
                  : uploadingPhoto
                  ? "Uploading photos..."
                  : "Add vehicle"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail / Edit Modal */}
      {showDetail && selectedTruck && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDetail(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "680px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "0.5px solid #e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              <div>
                <div className="t-name" style={{ fontSize: "15px" }}>
                  {selectedTruck.make} {selectedTruck.year} — Unit #
                  {selectedTruck.unit_number}
                </div>
                <div
                  className="t-label"
                  style={{ color: "#9ca3af", marginTop: "2px" }}
                >
                  {selectedTruck.license_plate} · {selectedTruck.state}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {!editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(true)}
                      style={{
                        background: "#f0f0f0",
                        color: "#555",
                        fontSize: "12px",
                        fontWeight: "500",
                        padding: "6px 14px",
                        borderRadius: "8px",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => setShowDetail(false)}
                      style={{
                        background: "#f0f0f0",
                        border: "none",
                        borderRadius: "8px",
                        width: "30px",
                        height: "30px",
                        cursor: "pointer",
                        fontSize: "16px",
                        color: "#555",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setForm({ ...DEFAULT_FORM, ...selectedTruck });
                        setPhotos([]);
                      }}
                      style={{
                        background: "#f0f0f0",
                        color: "#555",
                        fontSize: "12px",
                        fontWeight: "500",
                        padding: "6px 14px",
                        borderRadius: "8px",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      style={{
                        background: "#3a9e5f",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "500",
                        padding: "6px 14px",
                        borderRadius: "8px",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={{ padding: "20px 24px" }}>
              {editMode ? (
                <>
                  <FormContent
                    form={form}
                    setF={setF}
                    drivers={drivers}
                    photos={photos}
                    setPhotos={setPhotos}
                  />
                  {detailPhotos.length > 0 && (
                    <div style={{ marginTop: "16px" }}>
                      <div style={sectionHeaderStyle}>Existing Photos</div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        {detailPhotos.map((p) => (
                          <div
                            key={p.id}
                            style={{
                              position: "relative",
                              width: "100px",
                              height: "75px",
                            }}
                          >
                            <img
                              src={p.url}
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "0.5px solid #e8e8e8",
                              }}
                              onClick={() => setLightboxUrl(p.url)}
                            />
                            <button
                              onClick={() => deletePhoto(p.id)}
                              style={{
                                position: "absolute",
                                top: "3px",
                                right: "3px",
                                background: "rgba(163,45,45,0.8)",
                                border: "none",
                                borderRadius: "50%",
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                                color: "#fff",
                                fontSize: "11px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div>
                    <div style={sectionHeaderStyle}>Unit Details</div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      {[
                        { label: "Unit #", value: selectedTruck.unit_number },
                        {
                          label: "In Service Date",
                          value: selectedTruck.in_service_date,
                        },
                        { label: "VIN", value: selectedTruck.vin },
                        {
                          label: "License Plate",
                          value: selectedTruck.license_plate,
                        },
                        {
                          label: "State",
                          value: getStateName(selectedTruck.state),
                        },
                        { label: "Make", value: selectedTruck.make },
                        { label: "Year", value: selectedTruck.year },
                        { label: "Color", value: selectedTruck.color },
                        {
                          label: "Gross Weight",
                          value: selectedTruck.gross_weight,
                        },
                        {
                          label: "Number of Axles",
                          value: selectedTruck.num_axles,
                        },
                        {
                          label: "Unladen Weight",
                          value: selectedTruck.unladen_weight,
                        },
                        { label: "Ownership", value: selectedTruck.ownership },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <div style={labelStyle}>{label}</div>
                          <div className="t-data" style={{ color: "#111" }}>
                            {value || "—"}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: "12px" }}>
                      <div style={labelStyle}>Assigned Drivers</div>
                      <div className="t-data" style={{ color: "#111" }}>
                        {getDriverNames(selectedTruck.assigned_drivers)}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={sectionHeaderStyle}>Other Details</div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <div>
                        <div style={labelStyle}>Purchase Type</div>
                        <div className="t-data" style={{ color: "#111" }}>
                          {selectedTruck.purchase_type || "—"}
                        </div>
                      </div>
                      <div>
                        <div style={labelStyle}>Status</div>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            ...getStatusStyle(selectedTruck.status),
                          }}
                        >
                          {selectedTruck.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={sectionHeaderStyle}>In-Cab Inventory</div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      {[
                        { label: "Fuel Card", value: selectedTruck.fuel_card },
                        {
                          label: "Tolls & PrePass",
                          value: selectedTruck.tolls_prepass,
                        },
                        {
                          label: "ELD Device(s)",
                          value: selectedTruck.eld_device,
                        },
                        {
                          label: "Camera Device(s)",
                          value: selectedTruck.camera_device,
                        },
                        { label: "Other", value: selectedTruck.cab_other },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <div style={labelStyle}>{label}</div>
                          <div className="t-data" style={{ color: "#111" }}>
                            {value || "—"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={sectionHeaderStyle}>Stickers</div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <div>
                        <div style={labelStyle}>IFTA Sticker #</div>
                        <div className="t-data" style={{ color: "#111" }}>
                          {selectedTruck.ifta_sticker || "—"}
                        </div>
                      </div>
                      <div>
                        <div style={labelStyle}>HUT Sticker #</div>
                        <div className="t-data" style={{ color: "#111" }}>
                          {selectedTruck.hut_sticker || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {detailPhotos.length > 0 && (
                    <div>
                      <div style={sectionHeaderStyle}>Truck Photos</div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        {detailPhotos.map((p) => (
                          <div
                            key={p.id}
                            onClick={() => setLightboxUrl(p.url)}
                            style={{
                              width: "120px",
                              height: "90px",
                              cursor: "zoom-in",
                            }}
                          >
                            <img
                              src={p.url}
                              alt=""
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "8px",
                                border: "0.5px solid #e8e8e8",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedTruck.notes && (
                    <div>
                      <div style={sectionHeaderStyle}>Additional Notes</div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#111",
                          lineHeight: "1.6",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: selectedTruck.notes,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.9)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            cursor: "zoom-out",
          }}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              cursor: "pointer",
              fontSize: "20px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: "8px",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
